import React, { useRef, useState } from 'react';
import { useEventNotification } from '../../../../framework/services/event-notification';
import { ShopAppEvents } from '../../../events';
import { BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { IDoorInstallationModel } from '../../../../entities-generated/models/project/door-installation-model';
import { useNavigator } from '../../../../framework/services/navigation';
import { DoorType } from '../../../../entities-generated/common/door-type';
import { IInstallationSiteModel } from '../../../../entities-generated/models/project/installation-site-model';
import { toKebabCase } from '../../../../framework/utils/string';
import { AppRoutes, getAppRouteByPathname, getNextShoppingUrl } from '../../../app-routes';
import { ProjectService } from '../../../services/project';
import { MyProjectApiClient } from '../../../../services/my-project';
import { CustomerAccountService, useProjectConfiguration } from '../../../../services/customer-account';
import { ToastService } from '../../../../framework/services/toast';
import { MessageBarType } from '@fluentui/react';
import { useLocation } from 'react-router-dom';
import { CartPanel } from '../cart-panel';
import { StormDoorDetailDialog } from '../st-door-detail-dialog';
import { StormHandleDetailDialog } from '../st-handle-detail-dialog';
import { BwjConfirmationDialog, IConfirmationDialogDisplayProps } from '../../../../framework/components/bwj-confirmation-dialog';
import { BwjEventArgs } from '../../../../framework/entities/bwj-event';
import { SeeInPersonDialog } from '../../../components/see-in-person-dialog';

interface IProjectCartManagerProps {
  onChangeViewStateRequest: (viewState: BwjViewState) => void;
}

const subscriberId = 'Component.ProjectCartManager';

export const ProjectCartManager: React.FunctionComponent<IProjectCartManagerProps> = (props) => {
  const nav = useNavigator();
  const location = useLocation();
  const projectConfig = useProjectConfiguration(subscriberId);
  const [showProjectCart, setShowProjectCart] = useState(false);
  const [displayedDoorDetails, setDisplayedDoorDetails] = useState<{ door: IDoorInstallationModel, site: string } | undefined>(undefined);
  const [displayedHandleDetails, setDisplayedHandleDetails] = useState<{ door: IDoorInstallationModel, site: string } | undefined>(undefined);
  const [priceWarningArgs, setPriceWarningArgs] = useState<BwjEventArgs<IConfirmationDialogDisplayProps | undefined> | undefined>(undefined);
  const [showSeeInPersonDialog, setShowSeeInPersonDialog] = useState(false);
  const preventCartClose = useRef(false);
  
  useEventNotification(() => setShowProjectCart(true), [], ShopAppEvents.ShowShoppingCartPanel, subscriberId);

  const onProjectCartClose = () => {
    preventCartClose.current === false && setShowProjectCart(false);
  }

  const onDoorSelected = (door: IDoorInstallationModel, site: IInstallationSiteModel) => {
    preventCartClose.current = true;
    setDisplayedDoorDetails({ door: door, site: site.name });
    
    if(door.priceChange !== 0) {
      MyProjectApiClient.clearPriceChangeNotification(door.id)
      .then(resp => {
        CustomerAccountService.updateProjectConfiguration(resp);
      })
      .catch(() => {}); // fail silent
    }
  }

  const onHandleSelected = (door: IDoorInstallationModel, site: IInstallationSiteModel) => {
    preventCartClose.current = true;
    setDisplayedHandleDetails({ door: door, site: site.name });
  }

  const performDoorInstallationRemoval = (door: IDoorInstallationModel) => {
    setShowProjectCart(false);
    props.onChangeViewStateRequest(BwjViewState.Loading);

    MyProjectApiClient.removeDoorFromProject(door.id)
    .then(resp => {
      CustomerAccountService.updateProjectConfiguration(resp);
      const currentRoute = getAppRouteByPathname(location.pathname);
      if(currentRoute?.key === AppRoutes.selectStormDoor.key
        || currentRoute?.key === AppRoutes.selectStormHandle.key) {
        const mthSite = location.pathname.match(currentRoute.regex!);
        if(!!mthSite && mthSite.length > 1) {
          const currentInstall = ProjectService.getDoorInstallation(mthSite[1], DoorType.StormDoor, resp);
          if(!currentInstall || currentInstall.isExcluded) {
            nav.goTo(getNextShoppingUrl(resp));
          }
        } else { // shouldnt ever happen
          nav.goTo(getNextShoppingUrl(resp));
        }
      }

      setShowProjectCart(true);
    })
    .catch(() => {
      ToastService.show(MessageBarType.error, 'Error - Please try again');
    })
    .finally(() => props.onChangeViewStateRequest(BwjViewState.Ready));
  }

  const onRemoveDoorInstallation = (door: IDoorInstallationModel, site: IInstallationSiteModel) => {
    const numSelectedDoorsRemoved = !!door.selectedDoor ? 1 : 0;
    const numPossiblePriceIncreases =
      ProjectService.getActiveInstallationsWithSelectedDoorCount(projectConfig) - numSelectedDoorsRemoved;
    
    if(numPossiblePriceIncreases > 0) {
      setPriceWarningArgs(new BwjEventArgs({
        title: 'Price Changes',
        message: 'Our pricing is volume based, which means we are able to offer better deals when more doors are ordered. Removing this door from your project will result in slight price increases for your other selections.',
        proceedPrompt: 'Got it - proceed',
        cancelPrompt: 'Cancel',
        onProceedRequested: performDoorInstallationRemoval,
        context: door,
      }));
    } else {
      performDoorInstallationRemoval(door);
    }
  }

  const onRestoreDoorInstallation = (door: IDoorInstallationModel, site: IInstallationSiteModel) => {
    setShowProjectCart(false);
    props.onChangeViewStateRequest(BwjViewState.Loading);

    MyProjectApiClient.restoreDoorToProject(door.id)
    .then(resp => {
      CustomerAccountService.updateProjectConfiguration(resp);
      const currentRoute = getAppRouteByPathname(location.pathname);
      if(currentRoute?.key === AppRoutes.checkout.key) {
        nav.goTo(getNextShoppingUrl(resp));
      }

      setShowProjectCart(true);
    })
    .catch(() => {
      ToastService.show(MessageBarType.error, 'Error - Please try again');
    })
    .finally(() => props.onChangeViewStateRequest(BwjViewState.Ready));
  }

  const onDoorChangeRequested = (install: IDoorInstallationModel, site: string) => {
    onCloseDoorDetails();
    setShowProjectCart(false);
    if(install.doorType === DoorType.StormDoor) {
      ProjectService.registerConfigOrderException();
      nav.goTo(AppRoutes.selectStormDoor.url(toKebabCase(site)));
    }
  };

  const onCloseDoorDetails = () => {
    preventCartClose.current = false;
    setDisplayedDoorDetails(undefined);
  };

  const onHandleChangeRequested = (install: IDoorInstallationModel, site: string) => {
    onCloseHandleDetails();
    setShowProjectCart(false);
    if(install.doorType === DoorType.StormDoor) {
      nav.goTo(AppRoutes.selectStormHandle.url(toKebabCase(site)));
    }
  };

  const onCloseHandleDetails = () => {
    preventCartClose.current = false;
    setDisplayedHandleDetails(undefined);
  };

  return (
    <>
      <BwjConfirmationDialog
        displayArgs={priceWarningArgs}
        closeButtonAriaLabel={'Cancel removal from project and close dialog'}
      />
      <SeeInPersonDialog
        showDialog={showSeeInPersonDialog}
        onDismiss={() => setShowSeeInPersonDialog(false)}
      />
      <CartPanel
        showPanel={showProjectCart}
        projectConfig={projectConfig}
        onCartClose={onProjectCartClose}
        onDoorSelected={onDoorSelected}
        onHandleSelected={onHandleSelected}
        onExcludeInstallation={onRemoveDoorInstallation}
        onRestoreInstallation={onRestoreDoorInstallation}
      />
      <StormDoorDetailDialog
        info={displayedDoorDetails}
        onChangeRequested={onDoorChangeRequested}
        onCloseDialog={onCloseDoorDetails}
        onSeeInPersonRequested={() => setShowSeeInPersonDialog(true)}
      />
      <StormHandleDetailDialog
        info={displayedHandleDetails}
        onChangeRequested={onHandleChangeRequested}
        onCloseDialog={onCloseHandleDetails}
      />
    </>
  );
};
